import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Formik } from "formik";
import * as Yup from "yup";

import { Box, Button, CircularProgress, TextField } from "@mui/material";

import useAuth from "src/hooks/useAuth";
import useRefMounted from "src/hooks/useRefMounted";

const LoginJWT = () => {
  const { login } = useAuth();
  const [isOTPRequired, setIsOTPRequired] = useState(false);
  const isMountedRef = useRefMounted();
  const { t } = useTranslation();

  return (
    <Formik
      initialValues={{
        email: "",
        password: "",
        submit: null,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string().max(255).required(t("Username is required")),
        password: Yup.string()
          .max(255)
          .required(t("The password field is required")),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await login(values.email, values.password, values.otp);

          if (isMountedRef.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err) {
          if (err?.detail && Array.isArray(err?.detail)) {
            err.detail.forEach((message) => {
              if (message === "otp required") {
                setIsOTPRequired(true);
              }
            });
          }
          if (isMountedRef.current) {
            setStatus({ success: false });
            setErrors({ email: err.detail });
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            error={Boolean(touched.email && errors.email)}
            fullWidth
            margin="normal"
            autoFocus
            helperText={touched.email && errors.email}
            label={t("Username")}
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            type="email"
            value={values.email}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            margin="normal"
            helperText={touched.password && errors.password}
            label={t("Password")}
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            type="password"
            value={values.password}
            variant="outlined"
          />
          {isOTPRequired && (
            <TextField
              error={Boolean(touched.otp && errors.otp)}
              fullWidth
              margin="normal"
              autoFocus
              helperText={touched.otp && errors.otp}
              label={t("OTP Code")}
              name="otp"
              onBlur={handleBlur}
              onChange={handleChange}
              type="text"
              value={values.otp}
              variant="outlined"
            />
          )}
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
          ></Box>

          <Button
            sx={{
              mt: 3,
            }}
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
            disabled={isSubmitting}
            type="submit"
            fullWidth
            size="large"
            variant="contained"
          >
            {t("Sign in")}
          </Button>
        </form>
      )}
    </Formik>
  );
};

export default LoginJWT;
